// react
import * as React from 'react';

// internationalization
import { useTranslation } from 'react-i18next';

// material ui icons
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import EditOffRoundedIcon from '@mui/icons-material/EditOffRounded';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import SaveIcon from '@mui/icons-material/Save';

// material ui
import {
  Button,
  ButtonGroup,
  FormControl,
  MenuItem,
  OutlinedInput,
  Select,
  Stack,
  Tooltip,
} from '@mui/material';

export default function PresetSelector({ dataGridState, setDataGridState }) {
  const { t } = useTranslation();
  const presetList = React.useMemo(
    () => dataGridState?.presets ?? [],
    [dataGridState],
  );
  const preset = Math.min(
    dataGridState?.chosenPreset ?? 0,
    Math.max(0, presetList.length - 1),
  );

  const [isEditing, setIsEditing] = React.useState(false);
  // Track the edited name separately while editing; use derived value otherwise
  const [editingName, setEditingName] = React.useState('');

  const selectPreset = (index) => {
    setDataGridState((prev) => ({ ...prev, chosenPreset: index }));
  };

  const createPreset = () => {
    const newIndex = presetList.length;
    setDataGridState(() => ({
      chosenPreset: newIndex,
      presets: [
        ...presetList,
        {
          name: 'Default',
          visibilityList: {},
          density: 'standard',
          sortModel: [],
          filterModel: { items: [], quickFilterValues: [] },
        },
      ],
    }));
    setEditingName('Default');
    setIsEditing(true);
  };

  const deletePreset = () => {
    if (presetList.length <= 1) return;
    const newList = presetList.filter((_, i) => i !== preset);
    setDataGridState({ chosenPreset: 0, presets: newList });
  };

  const updatePresetName = (index, newName) => {
    const trimmed = newName.trim();
    if (!trimmed) return;
    const isDupe = presetList.some(
      (p, i) => i !== index && p.name.toLowerCase() === trimmed.toLowerCase(),
    );
    if (isDupe) return;

    setDataGridState((prev) => ({
      ...prev,
      chosenPreset: index,
      presets: prev.presets.map((p, i) =>
        i === index ? { ...p, name: trimmed } : p,
      ),
    }));
  };

  const handleSave = () => {
    updatePresetName(preset, editingName);
    setIsEditing(false);
  };

  const dirty =
    editingName.trim() !== (presetList[preset]?.name ?? '').trim() &&
    editingName.trim().length > 0;

  // --- Shared styles so Select and Text input look identical
  const leftFieldSx = {
    borderTopLeftRadius: 8,
    borderBottomLeftRadius: 8,
    borderTopRightRadius: 0,
    borderBottomRightRadius: 0,
    height: 40,
    backgroundColor: (t) => t.palette.background.paper,
    '& .MuiOutlinedInput-notchedOutline': { borderColor: 'divider' },
    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'divider' },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: 'divider',
    },
  };

  return (
    <Stack direction="row" alignItems="stretch" spacing={0} sx={{ mb: 0.5 }}>
      {/* Left control: Select (view) OR OutlinedInput (edit) — same visuals */}
      <FormControl
        size="small"
        sx={{ minWidth: 220, maxWidth: 320, flexShrink: 1 }}
      >
        {isEditing ? (
          <OutlinedInput
            value={editingName}
            onChange={(e) => setEditingName(e.target.value)}
            onBlur={() => {
              if (dirty) handleSave();
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                if (dirty) handleSave();
                e.preventDefault();
              }
              if (e.key === 'Escape') {
                setEditingName('');
                setIsEditing(false);
                e.preventDefault();
              }
            }}
            sx={leftFieldSx}
          />
        ) : (
          <Select
            value={preset}
            onChange={(e) => selectPreset(e.target.value)}
            // Pass an OutlinedInput so Select shares identical look & feel
            input={<OutlinedInput sx={leftFieldSx} />}
            IconComponent={ExpandMoreRoundedIcon}
            displayEmpty
            renderValue={(idx) => presetList[idx]?.name ?? ''}
            MenuProps={{ PaperProps: { sx: { maxHeight: 320, width: 260 } } }}
          >
            {presetList.map((item, index) => (
              <MenuItem key={index} value={index}>
                {item.name}
              </MenuItem>
            ))}
          </Select>
        )}
      </FormControl>

      {/* Right: actions as one fused ButtonGroup */}
      <ButtonGroup
        variant="outlined"
        sx={{
          backgroundColor: (t) => t.palette.background.paper,
          ml: '-1px', // fuse border seam with the left control
          '& .MuiButton-root': {
            height: 40,
            borderTopLeftRadius: 0,
            borderBottomLeftRadius: 0,
            borderColor: 'divider',
            ':hover': { borderColor: 'divider' },
          },
          '& .MuiButtonGroup-grouped:not(:last-of-type)': {
            borderRightColor: 'transparent',
          },
        }}
      >
        {isEditing ? (
          <>
            <Button
              onClick={() => {
                setEditingName('');
                setIsEditing(false);
              }}
              startIcon={<EditOffRoundedIcon />}
              sx={{ borderRadius: 0 }}
            >
              {t('actions.cancel')}
            </Button>
            <Tooltip
              title={dirty ? t('tooltips.saveName') : t('tooltips.noChanges')}
            >
              <span>
                <Button
                  onClick={handleSave}
                  disabled={!dirty}
                  startIcon={<SaveIcon />}
                  sx={{ borderRadius: 0 }}
                >
                  {t('actions.save')}
                </Button>
              </span>
            </Tooltip>
            <Tooltip
              title={
                presetList.length > 1
                  ? t('actions.delete')
                  : t('tooltips.cannotDeleteOnlyPreset')
              }
            >
              <span>
                <Button
                  color="error"
                  onClick={deletePreset}
                  disabled={presetList.length <= 1}
                  startIcon={<DeleteIcon />}
                  // sx={{ borderTopLeftRadius: 2, borderBottomLeftRadius: 2 }}
                >
                  {t('actions.delete')}
                </Button>
              </span>
            </Tooltip>
          </>
        ) : (
          <>
            <Tooltip title={t('actions.editItem', { item: 'preset' })}>
              <Button
                onClick={() => {
                  setEditingName(presetList[preset]?.name ?? '');
                  setIsEditing(true);
                }}
                startIcon={<EditIcon />}
                sx={{ borderRadius: 0 }}
              >
                {t('actions.edit')}
              </Button>
            </Tooltip>
            <Tooltip title={t('actions.addItem', { item: 'preset' })}>
              <Button
                onClick={createPreset}
                startIcon={<AddRoundedIcon />}
                // sx={{ borderTopLeftRadius: 2, borderBottomLeftRadius: 2 }}
              >
                {t('actions.add')}
              </Button>
            </Tooltip>
          </>
        )}
      </ButtonGroup>
    </Stack>
  );
}
